import DropdownSheet from 'components/Luxkit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { validateSemiForm } from 'lib/forms/formUtils'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import React, { ComponentProps, useCallback, useContext, useRef } from 'react'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelection from './OfferCapacityWithAgesSelection'
import OfferPageFormattedOccupancy from './OfferPageFormattedOccupancy'
import styled from 'styled-components'

const StyledOfferCapacityWithAgesSelectBox = styled(OfferCapacityWithAgesSelectBox)`
  &.elevated {
    position: relative;
    z-index: 3;
  }
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'boundaryRef' | 'open'> {
  capacities?: Array<App.PackageRoomCapacity>;
  occupantUnit?: 'guest' | 'traveller';
  childrenAgeLabel?: string
  onApply?: () => void
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disableMultiRoom?: boolean;
  className?: string;
  iconDisabled?: boolean;
  maxRooms?: number;
}

function OfferCapacityWithAgesSelect(props: Props) {
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel = 'at time of check-in',
    onApply,
    disableMultiRoom,
    onClick,
    boundaryRef,
    className,
    iconDisabled,
    maxRooms,
  } = props
  const trigger = useRef(null)
  const container = useRef<HTMLDivElement>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useIsMobileScreen()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const dismissOccupancyFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, trackEvent])

  const onApplyClose = useCallback(() => {
    if (validateSemiForm(dropdownRef.current!)) {
      dismissOccupancyFilter()
    }
    onApply?.()
  }, [dismissOccupancyFilter, onApply])

  const isDropdownOpen = open && !isMobile

  return (
    <div ref={container} className={className}>
      <StyledOfferCapacityWithAgesSelectBox
        ref={trigger}
        className={isDropdownOpen ? 'elevated' : undefined}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
        iconDisabled={iconDisabled}
      />
      <DropdownSheet
        ref={dropdownRef}
        data-testid="offer-capacity-with-ages-select-dropdown"
        anchorRef={trigger}
        triggerRef={trigger}
        boundaryRef={boundaryRef}
        size="L"
        title="Who is going?"
        open={isDropdownOpen}
        onClose={dismissOccupancyFilter}
        primaryActionProps={{
          children: 'Apply',
          onClick: onApplyClose,
        }}
        footerStart={<Group direction="horizontal-reverse">
          <BodyText variant="large"><OfferPageFormattedOccupancy /></BodyText>
        </Group>}
      >
        <OfferCapacityWithAgesSelection
          maxRooms={maxRooms}
          capacities={capacities}
          childrenAgeLabel={childrenAgeLabel}
          disableMultiRoom={disableMultiRoom}
        />
      </DropdownSheet>
    </div>
  )
}

export default withOfferPageState(
  (state) => ({
    open: state.activeFilter === 'capacity',
  }))(OfferCapacityWithAgesSelect)
